import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  InputNumber,
  Input,
  Row,
  Col,
  message,
  Typography,
  Divider,
  Space,
  DatePicker
} from 'antd';
import dayjs from 'dayjs';
import { inventoryAPI } from '../services/api.ts';
import { inputNumberFormatter, inputNumberParser } from '../utils/numberFormatting.ts';

const { Option } = Select;
const { Title } = Typography;

interface InventoryStock {
  id: number;
  product_id: number;
  warehouse_id: number;
  customer_id?: number;
  supplier_id?: number;
  stock_code?: string;
  date_stock?: string;
  customer_order_id?: number;
  supplier_order_id?: number;
  udc_pallet?: string;
  udc_collo?: string;
  qta_available: number;
  qta_reserved: number;
  qta_in_production: number;
  cost?: number;
  price?: number;
  weight_gross?: number;
  dogana_code?: string;
  created_at: string;
  updated_at: string;
  // Campi leggibili
  internal_product_code: string;
  product_description: string;
  customer_company?: string;
  supplier_name?: string;
  warehouse_description: string;
  total_quantity: number;
}

interface FilterOptions {
  products: Array<{id: number, internal_product_code: string, product_description: string, display_name: string}>;
  stock_codes: Array<{value: string}>;
  customers: Array<{id: number, company: string}>;
  suppliers: Array<{id: number, name: string}>;
  warehouses: Array<{id: number, description: string, cod_mag: string}>;
  customer_orders: Array<{id: number, order_number: string}>;
  supplier_orders: Array<{id: number, order_number: string}>;
}

interface InventoryStockModalProps {
  visible: boolean;
  stock: InventoryStock | null;
  filterOptions: FilterOptions | null;
  onOk: () => void;
  onCancel: () => void;
}

const InventoryStockModal: React.FC<InventoryStockModalProps> = ({
  visible,
  stock,
  filterOptions,
  onOk,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // Ordini cliente cercati lato server: l'elenco di filterOptions è troncato ai
  // 100 più recenti e non permette di collegare una giacenza a un ordine vecchio
  const [customerOrderOptions, setCustomerOrderOptions] = useState<Array<{id: number, order_number: string, customer_order_number?: string, reorder?: boolean}>>([]);
  const [searchingOrders, setSearchingOrders] = useState(false);

  const searchCustomerOrders = async (q?: string, includeId?: number) => {
    setSearchingOrders(true);
    try {
      const data = await inventoryAPI.searchCustomerOrders(q, includeId);
      setCustomerOrderOptions(data || []);
    } catch (error) {
      console.error('Error searching customer orders:', error);
    } finally {
      setSearchingOrders(false);
    }
  };

  useEffect(() => {
    if (visible) {
      searchCustomerOrders(undefined, stock?.customer_order_id);
      if (stock) {
        // Modalità modifica - popola il form con i dati esistenti
        form.setFieldsValue({
          product_id: stock.product_id,
          warehouse_id: stock.warehouse_id,
          customer_id: stock.customer_id,
          supplier_id: stock.supplier_id,
          stock_code: stock.stock_code,
          date_stock: stock.date_stock ? dayjs(stock.date_stock) : null,
          customer_order_id: stock.customer_order_id,
          supplier_order_id: stock.supplier_order_id,
          dogana_code: stock.dogana_code,
          udc_pallet: stock.udc_pallet,
          udc_collo: stock.udc_collo,
          qta_available: stock.qta_available,
          qta_reserved: stock.qta_reserved,
          qta_in_production: stock.qta_in_production,
          cost: stock.cost,
          price: stock.price,
          weight_gross: stock.weight_gross,
        });
      } else {
        // Modalità creazione - resetta il form con valori di default
        form.resetFields();
        form.setFieldsValue({
          qta_available: 0,
          qta_reserved: 0,
          qta_in_production: 0,
        });
      }
    }
  }, [visible, stock, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Conversione dei valori numerici per ID fields
      const { dogana_code, ...restValues } = values;
      const formData = {
        ...restValues,
        product_id: parseInt(restValues.product_id),
        warehouse_id: parseInt(restValues.warehouse_id),
        customer_id: restValues.customer_id ? parseInt(restValues.customer_id) : null,
        supplier_id: restValues.supplier_id ? parseInt(restValues.supplier_id) : null,
        customer_order_id: restValues.customer_order_id ? parseInt(restValues.customer_order_id) : null,
        supplier_order_id: restValues.supplier_order_id ? parseInt(restValues.supplier_order_id) : null,
        date_stock: restValues.date_stock ? restValues.date_stock.format('YYYY-MM-DD') : null,
        qta_available: restValues.qta_available || 0,
        qta_reserved: restValues.qta_reserved || 0,
        qta_in_production: restValues.qta_in_production || 0,
      };

      let result: any;
      if (stock) {
        // Aggiornamento stock esistente
        result = await inventoryAPI.updateInventoryStock(stock.id, formData);
        message.success('Stock aggiornato con successo');
      } else {
        // Creazione nuovo stock
        result = await inventoryAPI.createInventoryStock(formData);
        message.success('Stock creato con successo');
      }
      // Avviso quando il collegamento a un ordine stock non produce copertura
      // (prodotto non in ordine, riga già coperta, nessuna quantità disponibile)
      if (result?.warning) {
        message.warning(result.warning, 8);
      }

      onOk();
    } catch (error: any) {
      console.error('Error saving stock:', error);
      if (error.response?.data?.detail) {
        message.error(`Errore: ${error.response.data.detail}`);
      } else {
        message.error('Errore nel salvataggio dello stock');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0 }}>
          {stock ? 'Modifica Stock' : 'Nuovo Prodotto in Stock'}
        </Title>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width="80%"
      style={{ maxWidth: '80vw' }}
      okText={stock ? 'Aggiorna' : 'Crea'}
      cancelText="Annulla"
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
      >
        <Divider orientation="left">Informazioni Obbligatorie</Divider>
        
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="product_id"
              label="Prodotto"
              rules={[{ required: true, message: 'Seleziona un prodotto' }]}
            >
              <Select
                placeholder="Seleziona Prodotto"
                showSearch
                filterOption={(input, option) =>
                  (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {filterOptions?.products.map(product => (
                  <Option key={product.id} value={product.id}>
                    {product.display_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="warehouse_id"
              label="Magazzino"
              rules={[{ required: true, message: 'Seleziona un magazzino' }]}
              tooltip={stock ? "Il magazzino non è modificabile da qui: usare 'Trasferimento Magazzino' dalla pagina Inventario (registra il movimento di trasferimento)" : undefined}
            >
              <Select placeholder="Seleziona Magazzino" disabled={!!stock}>
                {filterOptions?.warehouses.map(warehouse => (
                  <Option key={warehouse.id} value={warehouse.id}>
                    {warehouse.description}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Informazioni Opzionali</Divider>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item name="customer_id" label="Cliente">
              <Select
                placeholder="Seleziona Cliente (opzionale)"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {filterOptions?.customers.map(customer => (
                  <Option key={customer.id} value={customer.id}>
                    {customer.company}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="supplier_id" label="Fornitore">
              <Select
                placeholder="Seleziona Fornitore (opzionale)"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {filterOptions?.suppliers.map(supplier => (
                  <Option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item
              name="stock_code"
              label="Stock Code"
              tooltip={stock ? "Lo Stock Code non è modificabile da qui: cambiarlo sull'ordine fornitore o sulla riga del documento di carico, che lo aggiornano anche sulle giacenze" : undefined}
            >
              <Input placeholder="Inserisci Stock Code (opzionale)" disabled={!!stock} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="dogana_code" label="Codice Dogana">
              <Input disabled placeholder="-" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="date_stock" label="Data Stock">
              <DatePicker
                style={{ width: '100%' }}
                placeholder="Seleziona Data Stock (opzionale)"
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item name="udc_pallet" label="UDC Pallet">
              <Input placeholder="Inserisci UDC Pallet (opzionale)" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="udc_collo" label="UDC Collo">
              <Input placeholder="Inserisci UDC Collo (opzionale)" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="customer_order_id"
              label="Ordine Cliente"
              tooltip="Collegando la giacenza a un ordine stock aperto, la merce entra automaticamente nella copertura di quell'ordine"
            >
              <Select
                placeholder="Cerca per numero ordine (opzionale)"
                allowClear
                showSearch
                loading={searchingOrders}
                filterOption={false}
                onSearch={(value) => searchCustomerOrders(value)}
                notFoundContent={searchingOrders ? 'Ricerca in corso...' : 'Nessun ordine trovato'}
              >
                {customerOrderOptions.map(order => (
                  <Option key={order.id} value={order.id}>
                    {order.order_number}
                    {order.reorder ? ' [STOCK]' : ''}
                    {order.customer_order_number ? ` · ${order.customer_order_number}` : ''}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="supplier_order_id" label="Ordine Fornitore">
              <Select
                placeholder="Seleziona Ordine Fornitore (opzionale)"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {filterOptions?.supplier_orders.map(order => (
                  <Option key={order.id} value={order.id}>
                    {order.order_number}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Quantità</Divider>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item name="qta_available" label="Quantità Disponibile">
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="0"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="qta_reserved" label="Quantità Riservata">
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="0"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="qta_in_production" label="Quantità In Produzione">
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="0"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Prezzi</Divider>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item name="cost" label="Costo">
              <InputNumber
                min={0}
                precision={4}
                style={{ width: '100%' }}
                formatter={inputNumberFormatter}
                parser={inputNumberParser}
                placeholder="0,0000"
                addonBefore="€"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="price" label="Vendita">
              <InputNumber
                min={0}
                precision={4}
                style={{ width: '100%' }}
                formatter={inputNumberFormatter}
                parser={inputNumberParser}
                placeholder="0,0000"
                addonBefore="€"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item name="weight_gross" label="Peso Lordo (kg)">
              <InputNumber
                min={0}
                precision={4}
                step={0.0001}
                style={{ width: '100%' }}
                formatter={inputNumberFormatter}
                parser={inputNumberParser}
                placeholder="0,0000"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default InventoryStockModal;